import {
  ACTIVE_REPORT_MS,
  ApiError,
  PROTOCOL_VERSION,
  api,
  isAppVersionSupported,
  type ConversationDetail,
  type EndpointOutput,
  type FileInfo,
  type HistoryMode,
  type Invitation,
  type Message,
  type Person,
  type ReplyRef,
  type ScheduledMessage,
} from '@sd/contracts';
import { AvatarCache } from './avatars';
import { errorCode, SIGN_OUT_CODES, type ClientContext, type ClientNotice, type SignOutReason } from './context';
import { Downloads, type PreviewSize } from './downloads';
import { createApiClient, type ApiClient } from './http';
import { LinkPreviews } from './linkPreviews';
import { mergeSummary, upsertMessages } from './merge';
import { Outbox, savedTrayKey } from './outbox';
import type { Platform, PlatformEvent } from './platform';
import { Reactions } from './reactions';
import { socketIoRealtime, type RealtimeFactory } from './realtime';
import { createClientStore, isDndOn, type ClientState, type ClientStore } from './state';
import { Sync, TYPING_REPEAT_MS } from './sync';
import { Timers } from './timers';
import { Uploads } from './uploads';

export interface MessengerClientOptions {
  platform: Platform;
  serverId: string;
  /** Defaults to an HTTP client for `platform.transport(serverId)`. */
  api?: ApiClient;
  /** Defaults to Socket.IO; pass `null` to run without realtime (tests/fixtures). */
  realtime?: RealtimeFactory | null;
  now?: () => Date;
  newId?: () => string;
  /** Is the window focused and visible? Used for read marking and notifications. */
  isFocused?: () => boolean;
}

export type DndMode = 'off' | '1h' | 'today';

/**
 * One signed-in session against one company server: REST + realtime sync,
 * outbox, uploads and conversation actions. Framework-agnostic; the UI reads
 * `store` and calls methods. Sync, outbox, uploads and downloads live in
 * their own modules that share one context.
 */
export class MessengerClient {
  readonly serverId: string;
  readonly store: ClientStore;
  readonly api: ApiClient;
  /** The app shell aggregates badges across servers. */
  badgeListener: ((count: number) => void) | null = null;
  private readonly ctx: ClientContext;
  private readonly sync: Sync;
  private readonly outbox: Outbox;
  private readonly uploads: Uploads;
  private readonly downloads: Downloads;
  private readonly reactions: Reactions;
  private readonly linkPreviews: LinkPreviews;
  private readonly avatars: AvatarCache;
  private readonly listeners = new Set<(notice: ClientNotice) => void>();
  private readonly unsubscribers: Array<() => void> = [];
  /** When "입력 중" was last sent, per conversation. */
  private readonly typingSentAt = new Map<string, number>();
  /** This app last told the server it is in use (push.active). */
  private activeReported = false;

  constructor(options: MessengerClientOptions) {
    this.serverId = options.serverId;
    this.api = options.api ?? createApiClient(options.platform.transport(options.serverId));
    this.store = createClientStore(options.serverId);
    this.ctx = {
      serverId: options.serverId,
      api: this.api,
      store: this.store,
      platform: options.platform,
      timers: new Timers(),
      now: options.now ?? (() => new Date()),
      newId: options.newId ?? (() => crypto.randomUUID()),
      isFocused: options.isFocused ?? (() => typeof document === 'undefined' || (document.visibilityState === 'visible' && document.hasFocus())),
      stopped: false,
      notice: (notice) => this.notice(notice),
      signOut: (reason) => this.signOut(reason),
      updateBadge: () => this.badgeListener?.(this.unreadTotal()),
    };
    this.outbox = new Outbox(this.ctx);
    this.uploads = new Uploads(this.ctx, this.outbox);
    this.sync = new Sync(this.ctx, options.realtime === undefined ? socketIoRealtime : options.realtime, this.outbox, this.uploads);
    this.uploads.afterSend = (conversationId) => this.sync.returnToLatest(conversationId);
    this.downloads = new Downloads(this.ctx);
    this.reactions = new Reactions(this.ctx);
    this.linkPreviews = new LinkPreviews(this.ctx);
    this.avatars = new AvatarCache(this.api, () => this.ctx.now().getTime());
  }

  get state(): ClientState {
    return this.store.getState();
  }

  private set(patch: Partial<ClientState> | ((s: ClientState) => Partial<ClientState>)) {
    this.store.setState(patch as Partial<ClientState>);
  }

  onNotice(listener: (notice: ClientNotice) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notice(notice: ClientNotice) {
    for (const listener of this.listeners) listener(notice);
  }

  /** Loads the session and initial data, then connects realtime. */
  async start(): Promise<'ready' | 'signed_out' | 'error'> {
    this.ctx.stopped = false;
    this.set({ status: 'starting', error: null });
    try {
      const [me, serverInfo, appInfo] = await Promise.all([this.api.call(api.users.me), this.api.call(api.meta.serverInfo), this.ctx.platform.appInfo()]);
      // A newer protocol or a minimum above this app: the app must be updated; an older protocol: the server must be.
      const unsupported =
        serverInfo.protocolVersion > PROTOCOL_VERSION || !isAppVersionSupported(appInfo.appVersion, serverInfo.supportedClients)
          ? 'UNSUPPORTED_CLIENT'
          : serverInfo.protocolVersion < PROTOCOL_VERSION
            ? 'UNSUPPORTED_SERVER'
            : null;
      if (unsupported) {
        this.set({ status: 'error', error: unsupported, serverInfo });
        return 'error';
      }
      this.set({ me, serverInfo });
      // Desktop do-not-disturb is one app-wide setting that main keeps across restarts.
      const settings = await this.ctx.platform.settings?.get().catch(() => null);
      if (settings?.dndUntil !== undefined) this.set({ dndUntil: settings.dndUntil });
      await this.outbox.restorePersisted(me.id);
      this.uploads.checkRestored();
      await this.sync.loadInitial();
      // Stopped while loading (the session was replaced): stay idle instead of connecting.
      if (this.ctx.stopped) {
        this.set({ status: 'idle' });
        return 'error';
      }
      this.set({ status: 'ready' });
    } catch (error) {
      const code = errorCode(error);
      if (SIGN_OUT_CODES.has(code)) {
        this.set({ status: 'signed_out', error: code });
        return 'signed_out';
      }
      this.set({ status: 'error', error: code });
      return 'error';
    }

    this.unsubscribers.push(this.ctx.platform.onEvent((event) => this.onPlatformEvent(event)));
    this.unsubscribers.push(
      this.store.subscribe((state, prev) => {
        const traysChanged = state.trays !== prev.trays && savedTrayKey(state.trays) !== savedTrayKey(prev.trays);
        if (state.pending !== prev.pending || state.drafts !== prev.drafts || traysChanged) this.outbox.schedulePersist();
        if (state.serverInfo?.state !== prev.serverInfo?.state) this.sync.serverStateChanged(prev.serverInfo?.state, state.serverInfo?.state);
      }),
    );
    this.sync.serverStateChanged(undefined, this.state.serverInfo?.state);
    this.sync.watchHealthForAdmins();
    this.sync.connectRealtime();
    const launched = this.ctx.platform.takeLaunchConversation?.();
    if (launched) void this.showConversation(launched).catch(() => {});
    void this.enablePush();
    this.ctx.timers.every('activity', ACTIVE_REPORT_MS, () => void this.reportActivity());
    void this.reportActivity();
    return 'ready';
  }

  /**
   * Tells the company server while the person is using this app outside do-not-disturb (`push.hold`), and once when
   * they stop: their pushes to other devices wait meanwhile, since this app shows the messages itself.
   */
  private async reportActivity() {
    const inUse = this.ctx.platform.inUse;
    if (!inUse || !this.state.serverInfo?.capabilities.includes('push.hold')) return;
    const active = !isDndOn(this.state, this.ctx.now()) && (await inUse().catch(() => false));
    if (!active && !this.activeReported) return;
    this.activeReported = active;
    await this.api.call(api.push.active, { body: { active } }).catch(() => {});
  }

  /**
   * Hands this browser's push subscription to the company server once notifications are allowed, and
   * again on every start (the browser may have renewed it). True when the server has it.
   */
  async enablePush(): Promise<boolean> {
    const push = this.ctx.platform.push;
    if (!push || push.permission() !== 'granted' || !this.state.serverInfo?.capabilities.includes('push.v1')) return false;
    try {
      const { publicKey, quietUntil } = await this.api.call(api.push.config);
      // The web keeps do-not-disturb in the tab: a reloaded page takes it back from the session.
      if (quietUntil && !this.state.dndUntil) this.set({ dndUntil: quietUntil });
      const subscription = await push.subscribe(publicKey);
      if (!subscription) return false;
      await this.api.call(api.push.subscribe, { body: subscription });
      return true;
    } catch {
      return false;
    }
  }

  stop() {
    this.ctx.stopped = true;
    this.sync.disconnect();
    for (const off of this.unsubscribers.splice(0)) off();
    this.ctx.timers.clearAll();
    this.uploads.stop();
    this.downloads.revokePreviews();
    this.linkPreviews.revokeAll();
    this.avatars.clear();
    // No longer connected or syncing: start() must run again before it is used.
    if (this.state.status === 'ready') this.set({ status: 'idle' });
  }

  /** Signs out of this server. Rejects, still signed in, when the host could not end the session. */
  async logout(): Promise<void> {
    await this.ctx.platform.auth.logout(this.serverId);
    await this.ctx.platform.persistence?.clear(this.serverId).catch(() => {});
    this.withdrawNotifications();
    this.stop();
    this.set({ status: 'signed_out', error: null });
    this.notice({ kind: 'signed_out', reason: 'logged_out' });
  }

  private signOut(reason: SignOutReason) {
    if (this.state.status === 'signed_out') return;
    // An expired session is the same person signing in again, so what they wrote waits for them (another account
    // wipes it on start); a revoked session or a deactivated account must not send anything later.
    if (reason === 'expired') void this.outbox.persistNow();
    else void this.ctx.platform.persistence?.clear(this.serverId).catch(() => {});
    this.withdrawNotifications();
    this.stop();
    this.set({ status: 'signed_out', error: reason });
    this.notice({ kind: 'signed_out', reason });
  }

  /** Message text shown in OS notifications must not outlive the session on a shared PC. */
  private withdrawNotifications() {
    void this.ctx.platform.withdrawNotifications?.(this.serverId).catch(() => {});
  }

  private onPlatformEvent(event: PlatformEvent) {
    switch (event.type) {
      case 'network.offline':
        this.set({ connection: 'offline' });
        break;
      case 'network.online':
      case 'power.resumed':
        this.sync.resume();
        break;
      case 'visibility':
        void this.reportActivity();
        break;
      case 'session.ended':
        if (event.serverId === this.serverId) this.signOut(event.reason === 'deactivated' ? 'deactivated' : event.reason === 'revoked' ? 'revoked' : 'expired');
        break;
      case 'settings.changed':
        if (event.settings.dndUntil !== undefined) this.set({ dndUntil: event.settings.dndUntil });
        break;
      case 'download.progress':
      case 'download.done':
      case 'download.failed':
        this.downloads.onDownloadEvent(event);
        break;
      default:
        break;
    }
  }

  /** Re-reads company name, limits and state; a changed `syncGeneration` means the database was restored. */
  refreshServerInfo(): Promise<void> {
    return this.sync.refreshServerInfo();
  }

  /** Catches up user events, the list and open timelines and sends the outbox, as after a reconnect. */
  resync(): Promise<void> {
    return this.sync.resync();
  }

  /** Fetches events after the timeline cursor until caught up. */
  catchUp(conversationId: string): Promise<void> {
    return this.sync.catchUp(conversationId);
  }

  /** Re-reads the signed-in profile (e.g. after an ownership transfer changed the role). */
  refreshMe(): Promise<void> {
    return this.sync.refreshMe();
  }

  loadPeople(): Promise<void> {
    return this.sync.loadPeople();
  }

  loadConversations(): Promise<void> {
    return this.sync.loadConversations();
  }

  loadDetail(conversationId: string): Promise<ConversationDetail> {
    return this.sync.loadDetail(conversationId);
  }

  loadConversationFiles(conversationId: string): Promise<FileInfo[]> {
    return this.sync.loadConversationFiles(conversationId);
  }

  /** Loads the newest page of conversations not opened yet, so opening them shows their messages at once. */
  prefetch(conversationIds: readonly string[]): Promise<void> {
    return this.sync.prefetch(conversationIds);
  }

  /** Opens a conversation (loads its newest page or the page around `messageId`). */
  openConversation(conversationId: string, options: { messageId?: string } = {}): Promise<void> {
    return this.sync.openConversation(conversationId, options);
  }

  /** Opens a conversation asked for from outside the screen (a notification): the screen also brings it to the front (`shown`). */
  showConversation(conversationId: string): Promise<void> {
    this.set((s) => ({ shown: { conversationId, nonce: (s.shown?.nonce ?? 0) + 1 } }));
    return this.openConversation(conversationId);
  }

  /** The screen brought `shown` to the front. */
  clearShown(nonce: number) {
    if (this.state.shown?.nonce === nonce) this.set({ shown: null });
  }

  loadOlder(conversationId: string): Promise<void> {
    return this.sync.loadOlder(conversationId);
  }

  /** Next page after a slice opened around an older message (search jump). */
  loadNewer(conversationId: string): Promise<void> {
    return this.sync.loadNewer(conversationId);
  }

  /** "최근 메시지로": back from an older slice to the newest messages. Rejects when that page cannot be loaded. */
  showLatest(conversationId: string): Promise<void> {
    return this.sync.showLatest(conversationId);
  }

  /** Called by the UI when the bottom of a conversation is (not) visible. */
  setViewing(conversationId: string | null, atBottom: boolean) {
    this.sync.setViewing(conversationId, atBottom);
  }

  /** Advances the read pointer to the newest loaded message (never backwards). */
  markRead(conversationId: string): Promise<void> {
    return this.sync.markRead(conversationId);
  }

  async openDirect(userId: string): Promise<string> {
    const res = await this.api.call(api.conversations.openDirect, { body: { userId } });
    this.sync.storeDetail(res.conversation);
    return res.conversation.id;
  }

  async createGroup(input: { memberIds: string[]; title?: string; fromDirectId?: string }): Promise<string> {
    const res = await this.api.call(api.conversations.createGroup, {
      body: { clientRequestId: this.ctx.newId(), memberIds: input.memberIds, ...(input.title ? { title: input.title } : {}), ...(input.fromDirectId ? { fromDirectId: input.fromDirectId } : {}) },
    });
    this.sync.storeDetail(res.conversation);
    return res.conversation.id;
  }

  async addMembers(conversationId: string, userIds: string[]): Promise<void> {
    const res = await this.api.call(api.conversations.addMembers, { params: { conversationId }, body: { userIds } });
    this.sync.storeDetail(res.conversation);
  }

  async setHistoryMode(conversationId: string, historyMode: HistoryMode): Promise<void> {
    const res = await this.api.call(api.conversations.update, { params: { conversationId }, body: { historyMode } });
    this.sync.storeDetail(res.conversation);
  }

  /** Group owner/admin only. */
  async renameGroup(conversationId: string, title: string): Promise<void> {
    const res = await this.api.call(api.conversations.update, { params: { conversationId }, body: { title } });
    this.sync.storeDetail(res.conversation);
  }

  /** Group owner only; the owner role itself moves when the owner leaves. */
  async setMemberRole(conversationId: string, userId: string, role: 'admin' | 'member'): Promise<void> {
    const res = await this.api.call(api.conversations.setMemberRole, { params: { conversationId, userId }, body: { role } });
    this.sync.storeDetail(res.conversation);
  }

  /** Group owner/admin only (an admin cannot remove another admin or the owner). */
  async removeMember(conversationId: string, userId: string): Promise<void> {
    const res = await this.api.call(api.conversations.removeMember, { params: { conversationId, userId } });
    this.sync.storeDetail(res.conversation);
  }

  async updateMine(conversationId: string, patch: { muted?: boolean; pinned?: boolean }): Promise<void> {
    const res = await this.api.call(api.conversations.updateMine, { params: { conversationId }, body: patch });
    const { muted, pinned } = res.conversation.my;
    this.sync.acceptSummary(res.conversation, (current) => {
      // This answer is authoritative for what was just changed, whatever else it is behind on.
      const merged = mergeSummary(current, res.conversation);
      return { ...merged, my: { ...merged.my, muted, pinned } };
    });
    this.ctx.updateBadge();
  }

  async leave(conversationId: string): Promise<void> {
    const res = await this.api.call(api.conversations.leave, { params: { conversationId } });
    this.sync.acceptSummary(res.conversation, (current) => mergeSummary(current, res.conversation));
    await this.sync.catchUp(conversationId).catch(() => {});
    void this.sync.loadDetail(conversationId).catch(() => {});
  }

  setDraft(conversationId: string, text: string) {
    if ((this.state.drafts[conversationId] ?? '') === text) return;
    this.set((s) => ({ drafts: { ...s.drafts, [conversationId]: text } }));
  }

  /**
   * Called as the user types in a conversation's input: tells the others "입력 중" at most every
   * TYPING_REPEAT_MS, where the server supports it and while connected. Best effort: a failure says nothing.
   */
  typing(conversationId: string) {
    if (!this.state.serverInfo?.capabilities.includes('typing.v1') || this.state.connection !== 'online') return;
    const now = this.ctx.now().getTime();
    if (now - (this.typingSentAt.get(conversationId) ?? -Infinity) < TYPING_REPEAT_MS) return;
    this.typingSentAt.set(conversationId, now);
    void this.api.call(api.conversations.typing, { params: { conversationId } }).catch(() => {});
  }

  /** Sends over REST whatever the socket state, behind any earlier message of the conversation that still waits. */
  sendMessage(conversationId: string, text: string, options: { replyToMessageId?: string | null; attachments?: FileInfo[]; mentions?: string[] } = {}): string {
    // The message ends "입력 중" for the others; typing on right after it is news again.
    this.typingSentAt.delete(conversationId);
    // Writing from an older slice (search jump) returns to the newest messages, where the new one appears.
    this.sync.returnToLatest(conversationId);
    return this.outbox.sendMessage(conversationId, text, options);
  }

  /** Sends everything still pending now. Resolves with how many were queued. */
  flushQueue(): Promise<number> {
    return this.outbox.flushQueue();
  }

  /** Stores pending sends, drafts and tray files now (desktop), e.g. before the app restarts into an update. */
  saveOutbox(): Promise<void> {
    return this.outbox.persistNow();
  }

  /** A draft, a send the server has not confirmed or a file in a tray: the web loses them with the page. */
  hasUnsent(): boolean {
    const s = this.state;
    return Object.keys(s.pending).length > 0 || Object.values(s.trays).some((items) => items.length > 0) || Object.values(s.drafts).some((text) => text.trim() !== '');
  }

  /** "다시 보내기": same clientMessageId, so a stored-but-unacknowledged send is not duplicated. */
  retry(clientMessageId: string) {
    this.outbox.retry(clientMessageId);
  }

  discard(clientMessageId: string) {
    this.outbox.discard(clientMessageId);
  }

  async editMessage(conversationId: string, messageId: string, text: string): Promise<void> {
    const message = this.state.timelines[conversationId]?.messages.find((m) => m.id === messageId);
    if (!message) throw new ApiError('NOT_FOUND', 'message not loaded');
    const res = await this.api.call(api.messages.edit, { params: { conversationId, messageId }, body: { text, baseRevision: message.revision } });
    this.acceptUpdated(res.message);
  }

  /** "예약 전송": text the server sends as the user at `sendAt` (`messages.scheduled`); throws what it refused. */
  async scheduleMessage(conversationId: string, text: string, sendAt: Date, mentions: string[] = []): Promise<ScheduledMessage> {
    const res = await this.api.call(api.messages.schedule, { params: { conversationId }, body: { text, mentions, sendAt: sendAt.toISOString() } });
    this.set((s) => ({ scheduled: { ...s.scheduled, [res.scheduled.id]: res.scheduled } }));
    return res.scheduled;
  }

  /** Not sent after all; throws CONFLICT when it went out (or failed) meanwhile. */
  async cancelScheduled(scheduledId: string): Promise<void> {
    await this.api.call(api.messages.cancelScheduled, { params: { scheduledId } });
    this.set((s) => {
      const { [scheduledId]: _gone, ...others } = s.scheduled;
      return { scheduled: others };
    });
  }

  /** "저장"/"저장 취소" (`messages.saved`): shows at once, and goes back if the server refuses (then throws). */
  async setSaved(conversationId: string, messageId: string, saved: boolean): Promise<void> {
    const apply = (on: boolean) =>
      this.set((s) => {
        const { [messageId]: _was, ...others } = s.saved;
        return { saved: on ? { ...others, [messageId]: true } : others };
      });
    const before = Boolean(this.state.saved[messageId]);
    apply(saved);
    try {
      await this.api.call(saved ? api.messages.save : api.messages.unsave, { params: { conversationId, messageId } });
    } catch (error) {
      apply(before);
      throw error;
    }
  }

  /** A page of "저장한 메시지", newest saved first; pass `nextBefore` for the next one. */
  listSaved(before?: string): Promise<EndpointOutput<typeof api.messages.saved>> {
    return this.api.call(api.messages.saved, { query: { ...(before ? { before } : {}), limit: 50 } });
  }

  /** "전달": the message's text and files into another conversation (`messages.forward`); throws what the server refused. */
  async forwardMessage(fromConversationId: string, messageId: string, toConversationId: string): Promise<Message> {
    const res = await this.api.call(api.messages.forward, { params: { conversationId: fromConversationId, messageId }, body: { clientMessageId: this.ctx.newId(), toConversationId } });
    this.acceptUpdated(res.message);
    return res.message;
  }

  async deleteMessage(conversationId: string, messageId: string): Promise<void> {
    const res = await this.api.call(api.messages.delete, { params: { conversationId, messageId } });
    this.acceptUpdated(res.message);
  }

  private acceptUpdated(message: Message) {
    const id = message.conversationId;
    this.set((s) => {
      const t = s.timelines[id];
      const c = s.conversations[id];
      const next: Partial<ClientState> = {};
      if (t) next.timelines = { ...s.timelines, [id]: { ...t, messages: upsertMessages(t.messages, [message]) } };
      if (c?.lastMessage?.messageId === message.id) {
        next.conversations = {
          ...s.conversations,
          [id]: { ...c, lastMessage: { ...c.lastMessage, kind: message.deletedAt ? 'deleted' : c.lastMessage.kind, text: message.deletedAt ? null : (message.text?.slice(0, 140) ?? null) } },
        };
      }
      return next;
    });
  }

  async readers(conversationId: string, messageId: string): Promise<{ read: Person[]; unread: Person[] }> {
    const res = await this.api.call(api.messages.readers, { params: { conversationId, messageId } });
    const people = this.state.people;
    const known = (id: string): Person | null => people[id] ?? null;
    return {
      read: res.read.map((r) => known(r.userId)).filter((p): p is Person => Boolean(p)),
      unread: res.unread.map(known).filter((p): p is Person => Boolean(p)),
    };
  }

  /** Adds the viewer's emoji to a message, or takes it back when it is there; the chips change at once and change back if the server refuses. */
  toggleReaction(conversationId: string, messageId: string, emoji: string): Promise<void> {
    return this.reactions.toggle(conversationId, messageId, emoji);
  }

  /** Everyone who reacted to a message, per emoji. */
  reactors(conversationId: string, messageId: string): Promise<Array<{ emoji: string; people: Person[] }>> {
    return this.reactions.list(conversationId, messageId);
  }

  /** Reply quote for a pending message, from the loaded timeline. */
  replyRefFor(conversationId: string, messageId: string): ReplyRef | null {
    const m = this.state.timelines[conversationId]?.messages.find((x) => x.id === messageId);
    if (!m) return null;
    return {
      messageId: m.id,
      senderId: m.senderId,
      excerpt: m.deletedAt ? null : (m.text?.slice(0, 120) ?? null),
      attachmentName: m.attachments[0]?.name ?? null,
      state: m.deletedAt ? 'deleted' : 'available',
    };
  }

  /** Adds files to the conversation's tray within the server's limits (size per file, files per send). */
  addFiles(conversationId: string, files: readonly File[]) {
    this.uploads.addFiles(conversationId, files);
  }

  /** Uploads a failed tray file again. */
  retryTrayItem(localId: string) {
    this.uploads.retryTrayItem(localId);
  }

  removeTrayItem(conversationId: string, localId: string) {
    this.uploads.removeTrayItem(conversationId, localId);
  }

  /**
   * "파일 보내기": sends the files that are ready (images together, other files one message each). Files still
   * uploading or being scanned go by themselves once their scan ends clean (`waiting`).
   */
  sendTray(conversationId: string): { sent: number; waiting: number } {
    return this.uploads.sendTray(conversationId);
  }

  download(file: FileInfo, options: { acknowledgeUnscanned?: boolean } = {}): Promise<void> {
    return this.downloads.download(file, options);
  }

  cancelDownload(fileId: string): Promise<void> {
    return this.downloads.cancel(fileId);
  }

  showInFolder(fileId: string): Promise<void> {
    return this.downloads.showInFolder(fileId);
  }

  /** Object URL for an inline image preview (fetched with this session's credentials). Call `releasePreview` once it is no longer shown. */
  previewUrl(fileId: string, options: { first?: boolean; size?: PreviewSize } = {}): Promise<string> {
    return this.downloads.previewUrl(fileId, options);
  }

  /** The preview is no longer shown; old unused ones are revoked so originals do not pile up in memory. */
  releasePreview(fileId: string, options: { size?: PreviewSize } = {}) {
    this.downloads.releasePreview(fileId, options);
  }

  /** Where the page's own video or audio element streams a playable file (web); null where the app downloads it instead. */
  mediaUrl(fileId: string): string | null {
    return this.ctx.platform.mediaUrl?.(fileId) ?? null;
  }

  /** Object URL for a link preview's image, from the company server. Call `releaseLinkThumbnail` once it is no longer shown. */
  linkThumbnailUrl(previewId: string): Promise<string> {
    return this.linkPreviews.thumbnailUrl(previewId);
  }

  releaseLinkThumbnail(previewId: string) {
    this.linkPreviews.releaseThumbnail(previewId);
  }

  /** "미리보기 제거" on an own message: the card goes for everyone, the link stays. */
  removeLinkPreview(conversationId: string, messageId: string, previewId: string): Promise<void> {
    return this.linkPreviews.remove(conversationId, messageId, previewId);
  }

  /** Object URL of someone's photo at `version` (fetched with this session's credentials). Call `releaseAvatar` once it is no longer shown. */
  avatarUrl(userId: string, version: string): Promise<string> {
    return this.avatars.avatarUrl(userId, version);
  }

  releaseAvatar(userId: string, version: string) {
    this.avatars.releaseAvatar(userId, version);
  }

  /** Sets the signed-in member's photo from a still JPEG, PNG or WebP; the server keeps its own square copy. */
  async setAvatar(image: Blob): Promise<void> {
    this.sync.acceptMe(await this.api.call(api.users.setAvatar, { body: image }));
  }

  async removeAvatar(): Promise<void> {
    this.sync.acceptMe(await this.api.call(api.users.removeAvatar));
  }

  /**
   * The signed-in member's name and department. Shown in every copy at once; colleagues and the
   * member's other devices get it from the `user.updated` that follows (which changes nothing here).
   */
  async updateProfile(patch: { displayName?: string; department?: string }): Promise<void> {
    this.sync.acceptMe(await this.api.call(api.users.updateMe, { body: patch }));
  }

  search(q: string, conversationId?: string) {
    return this.api.call(api.search.search, { query: { q, limit: 30, ...(conversationId ? { conversationId } : {}) } });
  }

  loadInvitations(): Promise<Invitation[]> {
    return this.sync.loadInvitations();
  }

  async invite(emails: string[]) {
    const res = await this.api.call(api.invitations.create, { body: { emails } });
    void this.sync.loadInvitations().catch(() => {});
    return res.results;
  }

  /** `link` only from a server that sends no mail, after approve or resend: the new one-time link to hand over. */
  async invitationAction(invitationId: string, action: 'approve' | 'reject' | 'resend' | 'revoke'): Promise<{ invitation: Invitation; link?: string }> {
    const endpoint = api.invitations[action];
    const res = await this.api.call(endpoint, { params: { invitationId } });
    this.set((s) => ({ invitations: s.invitations.map((i) => (i.id === invitationId ? res.invitation : i)) }));
    return res;
  }

  listSessions() {
    return this.api.call(api.auth.sessions).then((r) => r.sessions);
  }

  revokeSession(sessionId: string) {
    return this.api.call(api.auth.revokeSession, { params: { sessionId } });
  }

  revokeOtherSessions() {
    return this.api.call(api.auth.revokeOtherSessions);
  }

  /** Do-not-disturb; on desktop main stores it for the whole app and every server's client follows it. */
  setDnd(mode: DndMode) {
    const now = this.ctx.now();
    const until =
      mode === 'off'
        ? null
        : mode === '1h'
          ? new Date(now.getTime() + 3_600_000).toISOString()
          : new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1).toISOString();
    this.set({ dndUntil: until });
    void this.ctx.platform.setDoNotDisturb(until).catch(() => {});
    // Pushes reach a browser with the page closed: the server must know this browser is quiet.
    if (this.ctx.platform.push && this.state.serverInfo?.capabilities.includes('push.v1')) void this.api.call(api.push.quiet, { body: { until } }).catch(() => {});
  }

  dndActive(): boolean {
    return isDndOn(this.state, this.ctx.now());
  }

  /** Unread total excluding muted conversations (dock/taskbar badge). */
  unreadTotal(): number {
    return Object.values(this.state.conversations).reduce((sum, c) => sum + (c.my.muted ? 0 : c.unreadCount), 0);
  }
}
